import { InputField } from "components/designV2/inputfield/inputfield";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import type { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import { CustomIcon } from "components/icon/customIcon.component";
import { useTranslation } from "react-i18next";
import type { StaffProfileData } from "services/api/company/staff.service";
import { formatStaffPhoneNumber } from "../../../shared/staffPhoneNumber.logic";
import { formatPersonalIdentityNumber } from "../../../shared/staffPersonalIdentityNumber.logic";
import type { PersonalDetailsForm } from "../../profilePanel.contract";
import {
  FieldCard,
  FieldLabel,
  FieldsGrid,
  FieldValue,
  Section,
  SectionActionRow,
  SectionBox,
  SectionTitle,
  StatValueRow,
} from "../../layout/profilePanel.styled";

interface ProfilePanelProfileSectionProps {
  isEditMode: boolean;
  editedPersonalDetails: PersonalDetailsForm;
  profile: StaffProfileData;
  teamLabels: string[];
  hasStatistics: boolean;
  ratingLabel: string;
  ratingCountLabel: string;
  workedHoursLabel: string;
  teamOptions: SearchableDropdownOption[];
  onPersonalFieldChange: <TField extends keyof PersonalDetailsForm>(
    field: TField,
    value: PersonalDetailsForm[TField],
  ) => void;
  onOpenChangeTeamModal: () => void;
}

/**
 * Handles profile panel profile section.
 */
export const ProfilePanelProfileSection = (
  props: ProfilePanelProfileSectionProps,
): JSX.Element => {
  const { t } = useTranslation();

  return (
    <>
      <Section>
        <SectionTitle>{t("Staff.ProfilePanelPersonalDetails")}</SectionTitle>
        <SectionBox>
          <FieldsGrid
            style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
          >
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldPhoneNumberMandatory")}
                  hintText={t("Staff.AddStaffPanelHintPhoneNumberCountryCode")}
                  maxLength={15}
                  value={props.editedPersonalDetails.phoneNumber}
                  onChange={(phoneNumber) =>
                    props.onPersonalFieldChange("phoneNumber", phoneNumber)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {formatStaffPhoneNumber(props.profile.phoneNumber)}
                  </FieldValue>
                  <FieldLabel>{t("Staff.PhoneNumber")}</FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("OrganizationalStructure.FirstName")}
                  value={props.editedPersonalDetails.firstName}
                  onChange={(firstName) =>
                    props.onPersonalFieldChange("firstName", firstName)
                  }
                />
              ) : (
                <>
                  <FieldValue>{props.profile.firstName ?? "-"}</FieldValue>
                  <FieldLabel>
                    {t("OrganizationalStructure.FirstName")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("OrganizationalStructure.LastName")}
                  value={props.editedPersonalDetails.lastName}
                  onChange={(lastName) =>
                    props.onPersonalFieldChange("lastName", lastName)
                  }
                />
              ) : (
                <>
                  <FieldValue>{props.profile.lastName ?? "-"}</FieldValue>
                  <FieldLabel>
                    {t("OrganizationalStructure.LastName")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldEmail")}
                  type="email"
                  value={props.editedPersonalDetails.email}
                  onChange={(email) =>
                    props.onPersonalFieldChange("email", email)
                  }
                />
              ) : (
                <>
                  <FieldValue>{props.profile.email ?? "-"}</FieldValue>
                  <FieldLabel>{t("Staff.AddStaffPanelFieldEmail")}</FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldPersonalNumber")}
                  hintText={t("Staff.AddStaffPanelHintPersonalNumberFormat")}
                  value={props.editedPersonalDetails.personalIdentityNumber}
                  onChange={(personalIdentityNumber) =>
                    props.onPersonalFieldChange(
                      "personalIdentityNumber",
                      personalIdentityNumber,
                    )
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {formatPersonalIdentityNumber(
                      props.profile.personalIdentityNumber,
                    )}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldPersonalNumber")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
          </FieldsGrid>
        </SectionBox>
      </Section>

      {props.profile.description &&
        props.profile.description.trim().length > 0 && (
          <Section>
            <SectionTitle>{t("Staff.ProfilePanelDescription")}</SectionTitle>
            <SectionBox>
              <FieldsGrid style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
                <FieldCard>
                  <FieldValue>{props.profile.description}</FieldValue>
                </FieldCard>
              </FieldsGrid>
            </SectionBox>
          </Section>
        )}

      <Section>
        <SectionTitle>Team/s</SectionTitle>
        <SectionBox>
          {props.isEditMode ? (
            <FieldsGrid
              style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
            >
              <FieldCard>
                <SearchableDropdown
                  fullWidth
                  label={`${t("Staff.AddStaffPanelFieldTeam")} (${t("Staff.AddStaffPanelMandatory")})`}
                  options={props.teamOptions}
                  selectedValues={props.editedPersonalDetails.teamIds}
                  multiSelect
                  onSelectedValuesChange={(teamIds) =>
                    props.onPersonalFieldChange("teamIds", teamIds)
                  }
                  placeholder={t("Staff.AddStaffPanelPlaceholderNotAssigned")}
                />
              </FieldCard>
            </FieldsGrid>
          ) : (
            <>
              <FieldsGrid
                style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
              >
                <FieldCard>
                  <FieldValue>
                    {props.teamLabels.length > 0
                      ? props.teamLabels.join(", ")
                      : "-"}
                  </FieldValue>
                </FieldCard>
              </FieldsGrid>
              <SectionActionRow>
                <SecondaryButton
                  label={t("Staff.ProfilePanelChangeTeam")}
                  size="small"
                  onClick={props.onOpenChangeTeamModal}
                />
              </SectionActionRow>
            </>
          )}
        </SectionBox>
      </Section>

      <Section>
        <SectionTitle>{t("Staff.ProfilePanelStatistics")}</SectionTitle>
        <SectionBox>
          {props.hasStatistics ? (
            <FieldsGrid>
              <FieldCard>
                <StatValueRow>
                  <CustomIcon name="star-filled" size="16px" />
                  <FieldValue>{props.ratingLabel}</FieldValue>
                </StatValueRow>
                <FieldLabel>{t("Staff.ProfilePanelStatsRating")}</FieldLabel>
              </FieldCard>
              <FieldCard>
                <FieldValue>{props.ratingCountLabel}</FieldValue>
                <FieldLabel>{t("Staff.ProfilePanelStatsRatings")}</FieldLabel>
              </FieldCard>
              <FieldCard>
                <FieldValue>{props.workedHoursLabel}</FieldValue>
                <FieldLabel>
                  {t("OrganizationalStructure.WorkedHours")}
                </FieldLabel>
              </FieldCard>
            </FieldsGrid>
          ) : (
            <FieldValue>{t("Staff.ProfilePanelNoStatistics")}</FieldValue>
          )}
        </SectionBox>
      </Section>
    </>
  );
};
